import React, { createContext } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { useSharedTranscript } from '@utils/providers/shared-transcript-context.tsx';
import { useNumpadShortcutsDisabled } from '@utils/hooks/use-numpad-shortcuts-disabled.ts';

interface SharedTranscriptHotkeyContextType {}

const SharedTranscriptHotkeyContext = createContext<
  SharedTranscriptHotkeyContextType | undefined
>(undefined);

interface SharedTranscriptHotkeyProviderProps {
  children: React.ReactNode;
}

export function SharedTranscriptHotkeyProvider({
  children
}: SharedTranscriptHotkeyProviderProps) {
  const { searchInputRef, applyStyle } = useSharedTranscript();
  const { play, pause, isPlaying, rewind, forward, toggleLinkedPlayback } =
    useAudio();

  // #4029 — Profile → Keyboard flag. Authenticated visitors see their own
  // preference honoured even on a shared link; anonymous visitors fall
  // through to the default (numpad on).
  const numpadDisabled = useNumpadShortcutsDisabled();

  // space or numpad 5 play pause
  useHotkeys(['space', 'numpad5'], event => {
    // `numpad5` also matches the top-row 5, so check the physical key.
    if (event.key !== ' ' && (numpadDisabled || event.code !== 'Numpad5')) {
      return;
    }
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  });

  // F5 Play/Pause - works while editing (contentEditable)
  useHotkeys(
    ['f5'],
    event => {
      event.preventDefault();
      if (isPlaying) {
        pause();
      } else {
        play();
      }
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // F7 Rewind 5 seconds - works while editing (contentEditable)
  useHotkeys(
    ['f7'],
    event => {
      event.preventDefault();
      rewind(5);
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // F8 Forward 5 seconds - works while editing (contentEditable)
  useHotkeys(
    ['f8'],
    event => {
      event.preventDefault();
      forward(5);
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // option or ctrl + arrow left rewind 5 seconds
  useHotkeys(
    ['left', 'ctrl+left', 'meta+left'], // 'meta+left' is for Mac
    () => {
      rewind(5);
    }
  );

  // option or ctrl + right arrow forward 5 seconds
  useHotkeys(
    ['right', 'ctrl+right', 'meta+right'], // 'meta+right' is for Mac
    () => {
      forward(5);
    }
  );

  // Playback-rate keyboard shortcuts have been removed. They previously bound
  // ctrl+plus / ctrl+= / ctrl+minus (and meta+…) and called preventDefault(),
  // which blocked the browser's native zoom (Utrecht #2XXX — Cladder/Stoffels
  // couldn't Ctrl+- to zoom out the page, it changed the playback speed
  // instead). Users can adjust playback rate via the dropdown in the media
  // controls.
  // ctrl+f focus search input
  useHotkeys(
    ['ctrl+f', 'meta+f'],
    event => {
      event.preventDefault();
      searchInputRef?.current?.focus();
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // ctrl+l toggle linked playback
  useHotkeys(
    ['ctrl+l', 'meta+l'],
    event => {
      event.preventDefault();
      toggleLinkedPlayback();
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // ctrl+b apply bold style
  useHotkeys(
    ['ctrl+b', 'meta+b'],
    () => {
      applyStyle('bold');
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
      enableOnContentEditable: true
    }
  );

  // ctrl+i apply italic style
  useHotkeys(
    ['ctrl+i', 'meta+i'],
    () => {
      applyStyle('italic');
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
      enableOnContentEditable: true
    }
  );

  // ctrl+u apply underline style
  useHotkeys(
    ['ctrl+u', 'meta+u'],
    () => {
      applyStyle('underlined');
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
      enableOnContentEditable: true
    }
  );

  // ctrl+s apply strike style
  useHotkeys(
    ['ctrl+s', 'meta+s'],
    () => {
      applyStyle('strike');
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
      enableOnContentEditable: true
    }
  );

  return (
    <SharedTranscriptHotkeyContext.Provider value={{}}>
      {children}
    </SharedTranscriptHotkeyContext.Provider>
  );
}
